import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheck, FiClock } from "react-icons/fi";
import JsBarcode from "jsbarcode";
import { QRCodeCanvas } from "qrcode.react";

import { useSnackBar } from "../../contexts/snackbar";
import { paymentStatus } from "../../lib/api";
import { formatPrice } from "../../utils/formatPrice";
import { getDate } from "../../utils/getDate";
import {
  ApprovalSeal,
  Barcode,
  Body,
  Card,
  CodeBox,
  Field,
  FieldLabel,
  InfoText,
  LiveRegion,
  Page,
  Panel,
  PanelCentered,
  PanelSubtitle,
  PanelTitle,
  PixInstructions,
  PixLayout,
  PrimaryButton,
  QrFrame,
  ReadOnlyCode,
  ScanHint,
  ScanTitle,
  SecondaryButton,
  SecondaryRow,
  Shell,
  SuccessCircle,
  WaitingDot,
  WaitingLine,
} from "./style";

const POLL_INTERVAL = 5000;

const POLLED_PAYMENT_METHODS = ["pix", "card", "wallet"];

const PAID_STATUS = 4;

const SUCCESS_HOLD = 3000;

const QR_SIZE = 200;

export const FinishPurchase = () => {
  const { state: rechargeData } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();

  const [isExpired, setIsExpired] = useState(false);
  const [status, setStatus] = useState(-1);
  const statusRef = useRef(-1);

  const method = rechargeData?.payment_method;
  const price = rechargeData?.value;
  const qrCode = rechargeData?.pix?.qr_code;

  useEffect(() => {
    if (
      isExpired ||
      statusRef.current > 3 ||
      !POLLED_PAYMENT_METHODS.includes(method)
    ) {
      return;
    }

    let active = true;
    let timeoutId = null;

    // Encadeado, e não `setInterval`: consulta lenta se sobreporia à seguinte e
    // resposta atrasada sobrescreveria um código mais novo.
    const poll = async () => {
      let code = null;

      try {
        const response = await paymentStatus();
        code = response?.data?.code ?? null;
      } catch {
        code = null;
      }

      if (!active) return;

      if (code !== null) {
        statusRef.current = code;
        setStatus(code);
      }

      if (code > 3) return;

      timeoutId = setTimeout(poll, POLL_INTERVAL);
    };

    timeoutId = setTimeout(poll, POLL_INTERVAL);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [isExpired, method]);

  useEffect(() => {
    if (status !== PAID_STATUS) return;

    const timeoutId = setTimeout(() => navigate("/loading"), SUCCESS_HOLD);

    return () => clearTimeout(timeoutId);
  }, [status, navigate]);

  useEffect(() => {
    if (method !== "pix") return;

    const expiresAt = new Date(
      rechargeData.pix?.expiration_date_qrcode || ""
    ).getTime();

    // `setTimeout` com NaN dispara na hora e marcaria o Pix como expirado ao abrir.
    if (!Number.isFinite(expiresAt)) return;

    const timeoutId = setTimeout(
      () => setIsExpired(true),
      expiresAt - Date.now()
    );

    return () => clearTimeout(timeoutId);
  }, [method, rechargeData]);

  useEffect(() => {
    if (method !== "boleto") return;

    try {
      JsBarcode("#barcode", rechargeData.number, {
        format: "CODE128",
        margin: 1,
        lineColor: "#000",
        width: 1.3,
        height: 40,
        displayValue: false,
      });
    } catch (error) {
      console.error(error);
    }
  }, [method, rechargeData]);

  const handleCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();

    // `execCommand` devolve false quando não copia, sem lançar.
    try {
      if (document.execCommand("copy")) {
        showSnackBar(t("payment.codeCopied"), "success");
      } else {
        showSnackBar(t("payment.errorToCopy"), "error");
      }
    } catch {
      showSnackBar(t("payment.errorToCopy"), "error");
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadPdf = () => {
    const link = document.createElement("a");
    link.href = rechargeData.pdf;
    link.setAttribute("download", "boleto.pdf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!rechargeData) return null;

  const subtitle = [
    t("payment.summaryTitle"),
    Number.isFinite(price) ? formatPrice(price) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const alertMessage =
    status > PAID_STATUS
      ? `${t("payment.paymentNotConfirmed")} ${t("payment.errorToPay")}`
      : isExpired
      ? `${t("payment.expiredPayment")} ${t("payment.expiredPaymentNote")}`
      : "";

  const waitingLine = (
    <WaitingLine>
      <WaitingDot aria-hidden="true" />
      {t("payment.waitingPayment")}
    </WaitingLine>
  );

  let card;

  if (status === PAID_STATUS) {
    card = (
      <PanelCentered>
        <SuccessCircle aria-hidden="true">
          <FiCheck size={26} />
        </SuccessCircle>
        <PanelTitle>{t("payment.paymentConfirmed")}</PanelTitle>
        <PanelSubtitle>{t("payment.paymentConfirmedNote")}</PanelSubtitle>
      </PanelCentered>
    );
  } else if (status > PAID_STATUS) {
    card = (
      <>
        <PanelCentered>
          <PanelTitle>{t("payment.paymentNotConfirmed")}</PanelTitle>
          <PanelSubtitle>{t("payment.errorToPay")}</PanelSubtitle>
        </PanelCentered>
        <Body>
          <PrimaryButton type="button" onClick={() => navigate("/checkout")}>
            {t("payment.tryAgain")}
          </PrimaryButton>
        </Body>
      </>
    );
  } else if (isExpired) {
    card = (
      <>
        <Panel>
          <PanelTitle>{t("payment.expiredPayment")}</PanelTitle>
          <PanelSubtitle>{t("payment.expiredPaymentNote")}</PanelSubtitle>
        </Panel>
        <Body>
          <PrimaryButton
            type="button"
            onClick={() => navigate("/checkout/pix")}
          >
            {t("payment.newPixCode")}
          </PrimaryButton>
        </Body>
      </>
    );
  } else if (method === "boleto") {
    card = (
      <>
        <Panel>
          <PanelTitle>{t("payment.boleto")}</PanelTitle>
          <PanelSubtitle>{subtitle}</PanelSubtitle>
        </Panel>
        <Body>
          <ApprovalSeal>
            <FiClock aria-hidden="true" />
            {t("payment.boletoDelay")}
          </ApprovalSeal>
          <InfoText>
            {t("payment.receiveBuy")}
            {rechargeData.title
              ? ` ${t("payment.in")} ${rechargeData.title}`
              : ""}
            . {t("payment.processing")}
          </InfoText>
          {rechargeData.expires_at && (
            <FieldLabel>
              {`${t("payment.expireIn")} ${getDate(rechargeData.expires_at)}`}
            </FieldLabel>
          )}
          <Barcode id="barcode" />
          <Field>
            <FieldLabel>{t("payment.boletoNumber")}</FieldLabel>
            <ReadOnlyCode readOnly value={rechargeData.number || ""} />
          </Field>
          <SecondaryRow>
            <SecondaryButton type="button" onClick={handleDownloadPdf}>
              {t("payment.downloadPdf")}
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={() => handleCopy(rechargeData.number)}
            >
              {t("payment.copyNumber")}
            </SecondaryButton>
          </SecondaryRow>
        </Body>
      </>
    );
  } else if (method === "pix") {
    card = (
      <>
        <Panel>
          <PanelTitle>{t("payment.payWithPix")}</PanelTitle>
          <PanelSubtitle>{subtitle}</PanelSubtitle>
        </Panel>
        <Body>
          <ApprovalSeal>
            <FiClock aria-hidden="true" />
            {t("payment.pixApprovalNote")}
          </ApprovalSeal>
          <PixLayout>
            {qrCode && (
              <QrFrame>
                <QRCodeCanvas value={qrCode} size={QR_SIZE} />
              </QrFrame>
            )}
            <PixInstructions>
              <ScanTitle>{t("payment.scanTitle")}</ScanTitle>
              <ScanHint>{t("payment.scanHint")}</ScanHint>
              {qrCode && (
                <>
                  <CodeBox>{qrCode}</CodeBox>
                  <PrimaryButton
                    type="button"
                    onClick={() => handleCopy(qrCode)}
                  >
                    {t("payment.copyPixCode")}
                  </PrimaryButton>
                </>
              )}
            </PixInstructions>
          </PixLayout>
          {waitingLine}
        </Body>
      </>
    );
  } else {
    card = (
      <>
        <PanelCentered>
          <PanelTitle>{t("payment.confirmingPayment")}</PanelTitle>
          <PanelSubtitle>{t("payment.confirmingPaymentNote")}</PanelSubtitle>
        </PanelCentered>
        <Body>{waitingLine}</Body>
      </>
    );
  }

  return (
    <Page>
      <Shell>
        {/* Persistentes e vazias: região viva inserida junto com o texto não é
            anunciada. */}
        <LiveRegion aria-live="polite">
          {status === PAID_STATUS
            ? `${t("payment.paymentConfirmed")} ${t(
                "payment.paymentConfirmedNote"
              )}`
            : ""}
        </LiveRegion>
        <LiveRegion role="alert">{alertMessage}</LiveRegion>
        <Card>{card}</Card>
      </Shell>
    </Page>
  );
};
